import { createSelector } from 'reselect';
import {
  path,
  reduce,
  keys,
  prop,
  propOr,
  find,
  propEq,
  filter,
  isNil,
  pick,
  pathEq,
  pathOr,
  pipe,
  replace,
  mergeLeft,
  pickBy,
} from 'ramda';
import { FORM_NAMES } from 'constants/forms';
import { DOCTOR } from 'constants/userRoles';
import { NOT_APPLICABLE } from 'constants/common';

import {
  getObservationValue,
  getObservationShockValues,
  getDetailedReportObservationValue,
  isComponentCodeDetailedReportFormField,
  removeAtrialAdditionalValues,
  removeVentricularAdditionalValues,
} from 'utilities/fhir/observation';
import { getPatientPageUrlId } from 'store/selectors/router';
import { getSiteEmail } from 'store/selectors/organization';

const newReportState = prop('newReport');
const patientState = prop('patientsData');

export const getNewReportStep = createSelector(
  newReportState,
  prop('step'),
);

export const getTransmissionType = createSelector(
  newReportState,
  propOr('', 'transmissionType'),
);

export const getUnscheduledVisitReason = createSelector(
  newReportState,
  propOr(null, 'unscheduledVisitReason'),
);

export const getParcingPdfData = createSelector(
  newReportState,
  prop('pdfParsingData'),
);

export const getTransmissionReportSummaryInitialValues = createSelector(
  getParcingPdfData,
  getSiteEmail,
  (parcingData, siteEmail) =>
    mergeLeft({
      anyVentricularArrhythmiasDetected: 'No',
      anyAtrialArrhythmiasDetected: 'No',
      percentAtrialPacing: NOT_APPLICABLE,
      shockNa: parcingData.shock === NOT_APPLICABLE,
      deviceClinicEmail: siteEmail,
    })(parcingData),
);

export const getParcingPdfLoading = createSelector(
  newReportState,
  prop('parsingDataLoading'),
);

export const getDeviceVendor = createSelector(
  patientState,
  getPatientPageUrlId,
  (patientData, patientId) =>
    pipe(
      path([patientId, 'data', 'extension']),
      find(propEq('url', 'device.name')),
      prop('valueCode'),
    )(patientData),
);

export const getPractitionerListByPatient = createSelector(
  patientState,
  getPatientPageUrlId,
  (patientData, patientId) => path([patientId, 'data', 'generalPractitioner'], patientData),
);

export const getPatientDoctors = createSelector(
  getPractitionerListByPatient,
  filter(pathEq(['identifier', 'value'], DOCTOR)),
);

export const getCommonReportFormValues = createSelector(
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values']),
  pick([
    'sentOnDate',
    'sentOnHour',
    'alert',
    'batteryLife',
    'lastUpdated',
    'pacing',
    'shock',
    'deviceClinicEmail',
  ]),
);

export const getAtrialRadioValue = createSelector(
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values']),
  prop('anyAtrialArrhythmiasDetected'),
);

export const getVentricularRadioValue = createSelector(
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values']),
  prop('anyVentricularArrhythmiasDetected'),
);

export const getDetailedEPReportFormValues = createSelector(
  getAtrialRadioValue,
  getVentricularRadioValue,
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values']),
  (atrialRadioValue, ventricularRadioValue, formValues) =>
    pipe(
      pickBy((value, key) => isComponentCodeDetailedReportFormField(key)),
      pickBy((value, key) => removeAtrialAdditionalValues(key, atrialRadioValue)),
      pickBy((value, key) => removeVentricularAdditionalValues(key, ventricularRadioValue)),
    )(formValues),
);

export const getShocksReportFormValues = createSelector(
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values', 'shocksDate']),
  filter(val => !isNil(val)),
);

export const getChoosenDoctor = createSelector(
  pathOr({}, ['form', FORM_NAMES.transmissionReportSummary, 'values']),
  propOr('', 'doctor'),
);

export const getChoosenDoctorId = createSelector(
  getChoosenDoctor,
  replace('Practitioner/', ''),
);

export const getPractitioners = createSelector(
  getChoosenDoctor,
  getPractitionerListByPatient,
  (doctorReference, practitioners) =>
    filter(
      ({ identifier: { value } = {}, reference }) =>
        value !== DOCTOR || reference === doctorReference,
      practitioners,
    ),
);

export const getPdfReportFileObject = createSelector(
  newReportState,
  prop('pdfBase64'),
);

export const getCreatedReportData = createSelector(
  getCommonReportFormValues,
  getShocksReportFormValues,
  getPractitioners,
  getTransmissionType,
  getDetailedEPReportFormValues,
  getUnscheduledVisitReason,
  (
    form,
    recordedShocks,
    practitioners,
    transmissionType,
    detailedValues,
    unscheduledVisitReason,
  ) => ({
    componentData: [].concat(
      reduce((acc, k) => [...acc, ...getObservationValue(form[k], k)], [], keys(form)),
      reduce(
        (acc, k) => [...acc, ...getDetailedReportObservationValue(detailedValues[k], k)],
        [],
        keys(detailedValues),
      ),
      getObservationShockValues(recordedShocks),
      [
        {
          code: {
            coding: [
              {
                code: 'transmission_type',
                display: 'Transmission type',
              },
            ],
          },
          valueString: transmissionType,
        },
        {
          code: {
            coding: [
              {
                code: 'unscheduled_visit_reason',
                display: 'Unscheduled Visit Reason',
              },
            ],
          },
          valueString: unscheduledVisitReason || NOT_APPLICABLE,
        },
      ],
    ),
    practitioners,
  }),
);
